import { useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, signInUrl } from '../lib/api.ts';
import { messageText } from '../lib/i18n.ts';
import { useSession } from '../lib/session.tsx';
// (SafeLink renders with VisuallyHidden from this module: the two only meet at render time, so the import cycle is harmless)
import { SafeLink } from './SafeLink.tsx';

/**
 * The accessible component kit (roadmap 3.6, WCAG 2.1 AA): native elements first (button, label, fieldset,
 * dialog), every control labelled, errors tied to their field with aria-describedby and announced, 44 px
 * targets, visible focus (styles/app.css). Nothing here renders HTML from a string.
 */

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="visually-hidden">{children}</span>;
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'danger' | 'ghost'; busy?: boolean; block?: boolean };

export function Button({ variant = 'primary', busy = false, block = false, className, children, disabled, type = 'button', ...rest }: ButtonProps) {
  const cls = ['btn', variant !== 'primary' ? `btn--${variant}` : '', block ? 'btn--block' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <button type={type} className={cls} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {children}
    </button>
  );
}

type FieldBase = { label: ReactNode; hint?: ReactNode; error?: string | null; required?: boolean };

function useFieldIds(hint: unknown, error: unknown) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return { id, hintId, errorId, describedBy: [hintId, errorId].filter(Boolean).join(' ') || undefined };
}

function FieldFrame({ ids, label, hint, error, required, children }: FieldBase & { ids: ReturnType<typeof useFieldIds>; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="field">
      <label className="field__label" htmlFor={ids.id}>
        {label}
        {required ? <span className="required-mark" aria-hidden="true"> *</span> : null}
        {required ? <VisuallyHidden> ({t('common.required')})</VisuallyHidden> : null}
      </label>
      {hint ? <span className="field__hint" id={ids.hintId}>{hint}</span> : null}
      {children}
      {error ? <span className="field__error" id={ids.errorId}>{error}</span> : null}
    </div>
  );
}

export function TextField({ label, hint, error, required, className, ...rest }: FieldBase & Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>) {
  const ids = useFieldIds(hint, error);
  return (
    <FieldFrame ids={ids} label={label} hint={hint} error={error ?? null} required={required ?? false}>
      <input id={ids.id} className={`input ${className ?? ''}`} aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy} required={required} {...rest} />
    </FieldFrame>
  );
}

export function TextArea({ label, hint, error, required, className, ...rest }: FieldBase & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'>) {
  const ids = useFieldIds(hint, error);
  return (
    <FieldFrame ids={ids} label={label} hint={hint} error={error ?? null} required={required ?? false}>
      <textarea id={ids.id} className={`textarea ${className ?? ''}`} aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy} required={required} {...rest} />
    </FieldFrame>
  );
}

export function SelectField<V extends string>({ label, hint, error, required, value, options, onChange, disabled }: FieldBase & {
  value: V | ''; options: readonly { value: V; label: string }[]; onChange(v: V): void; disabled?: boolean;
}) {
  const ids = useFieldIds(hint, error);
  const { t } = useTranslation();
  return (
    <FieldFrame ids={ids} label={label} hint={hint} error={error ?? null} required={required ?? false}>
      <select id={ids.id} className="select" value={value} disabled={disabled} required={required} aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy} onChange={(e) => onChange(e.currentTarget.value as V)}>
        {value === '' ? <option value="" disabled>{t('common.choose')}</option> : null}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </FieldFrame>
  );
}

export function Checkbox({ label, checked, onChange, disabled, describedBy }: {
  label: ReactNode; checked: boolean; onChange(v: boolean): void; disabled?: boolean; describedBy?: string;
}) {
  const id = useId();
  return (
    <div className="check">
      <input id={id} type="checkbox" checked={checked} disabled={disabled} aria-describedby={describedBy}
        onChange={(e) => onChange(e.currentTarget.checked)} />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

export function RadioGroup<V extends string>({ legend, name, value, options, onChange }: {
  legend: ReactNode; name: string; value: V; options: { value: V; label: ReactNode }[]; onChange(v: V): void;
}) {
  const id = useId();
  return (
    <fieldset>
      <legend>{legend}</legend>
      {options.map((o) => (
        <div className="check" key={o.value}>
          <input id={`${id}-${o.value}`} type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
          <label htmlFor={`${id}-${o.value}`}>{o.label}</label>
        </div>
      ))}
    </fieldset>
  );
}

/** role=alert for errors and warnings that need attention now; role=status for everything else. */
export function Alert({ tone = 'info', title, children }: { tone?: 'info' | 'error' | 'warning' | 'success'; title?: ReactNode; children?: ReactNode }) {
  const urgent = tone === 'error' || tone === 'warning';
  return (
    <div className={`alert alert--${tone}`} role={urgent ? 'alert' : 'status'}>
      {title ? <p><strong>{title}</strong></p> : null}
      {children}
    </div>
  );
}

/**
 * An API failure as text in the person's language (message keys only; the server never sends prose). A sensitive action
 * refused for want of a recent sign-in (REAUTH_REQUIRED, ASVS 3.7.1) offers the step-up sign-in, back to this page.
 */
export function ErrorAlert({ error }: { error: unknown }) {
  if (!error) return null;
  const key = error instanceof ApiError ? error.messageKey : 'err.internal';
  if (error instanceof ApiError && error.code === 'REAUTH_REQUIRED') return <StepUpPrompt message={messageText(key)} />;
  return <Alert tone="error"><p>{messageText(key)}</p></Alert>;
}

function StepUpPrompt({ message }: { message: string }) {
  const { t } = useTranslation();
  const { state } = useSession();
  const mode = state.status === 'signedIn' ? state.view.mode : 'standard';
  const here = `${globalThis.location.pathname}${globalThis.location.search}`;
  return (
    <Alert tone="warning">
      <p>{message}</p>
      <p><SafeLink className="btn" href={signInUrl(mode, here, true)} document data-testid="step-up">{t('stepUp.button')}</SafeLink></p>
    </Alert>
  );
}

export function Loading({ label }: { label?: string }) {
  const { t } = useTranslation();
  return <p className="page-loading" role="status">{label ?? t('common.loading')}</p>;
}

/**
 * A modal dialog on the native <dialog> element: the browser makes the rest of the page inert, keeps focus
 * inside, moves focus to the first control on open and back to where it was on close. Escape calls
 * `onCancel` (the dialog stays open unless the owner closes it).
 */
export function Dialog({ open, title, children, actions, onCancel, alert = false }: {
  open: boolean; title: ReactNode; children: ReactNode; actions: ReactNode; onCancel?: () => void; alert?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    else if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const cancel = (e: Event) => {
      e.preventDefault();
      onCancelRef.current?.();
    };
    d.addEventListener('cancel', cancel);
    return () => d.removeEventListener('cancel', cancel);
  }, []);

  return (
    <dialog ref={ref} className="dialog" role={alert ? 'alertdialog' : undefined} aria-modal="true"
      aria-labelledby={`${id}-title`} aria-describedby={`${id}-body`}>
      <div className="dialog__body">
        <h2 id={`${id}-title`}>{title}</h2>
        <div id={`${id}-body`}>{children}</div>
      </div>
      <div className="dialog__actions">{actions}</div>
    </dialog>
  );
}

/**
 * Lists keyed by entity id (roadmap 3.6, prevents F-11): React keeps each row's DOM and state, so a refresh
 * that reorders or adds rows never wipes what someone is typing in one of them. Keys must be stable ids,
 * never array positions.
 */
export function KeyedList<T>({ items, getKey, children, className, label }: {
  items: readonly T[]; getKey(item: T): string; children(item: T): ReactNode; className?: string; label?: string;
}) {
  if (import.meta.env.DEV) {
    const keys = items.map(getKey);
    if (new Set(keys).size !== keys.length) console.error('KeyedList: duplicate keys', keys);
  }
  return (
    <ul className={className ?? 'list'} aria-label={label}>
      {items.map((item) => <li key={getKey(item)}>{children(item)}</li>)}
    </ul>
  );
}
